import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { CHAT_ENDPOINT } from '../constants';
import { marked } from 'marked';
import tailwindStyles from '../../static/output.css'

const DEFAULT_LOCALE = {
  placeholder: 'Ask about our products...',
  send: 'Send',
  thinking: 'Thinking...',
  addedToCart: 'Added to cart!',
  addToCart: 'Add to Cart',
  buyNow: 'Buy Now',
  retryMsg: '⚠️ Something went wrong. Please try again.',
  newChat: 'New Chat',
};

@customElement('ai-sales-concierge-chat')
export class AISalesConciergeChat extends LitElement {

  @property({ type: String }) myshopifydomain = null;
  @property({ type: String }) chatTitle = 'Assistant';
  @property({ type: String }) openingMessage = 'I can help you find the right product!';
  @property({ type: String }) colorscheme = 'light';
  @property({ type: Object }) locale = DEFAULT_LOCALE;
  @property({ type: String }) avatarurl = '/static/images/avatar-female.png';

  @state() messages = [];
  @state() input = '';
  @state() loading = false;
  @state() thinking = false;
  @state() minimized = true;

  ws: WebSocket | null = null;

  // For tracking product card collapse state
  @state() recommendedProductStates = [];
  @state() selectedVariants = []; // parallel to messages/recommendedProductStates

  @state() wsError = false;

  firstUpdated() {
    const sheet = new CSSStyleSheet();
    // convert to em so it looks the same on all shopify stores regardless of rem value
    const emStyles = tailwindStyles.replaceAll(/(?<=\d)rem/g, 'em')
    sheet.replaceSync(emStyles);
    this.renderRoot.adoptedStyleSheets = [sheet];

    (window as any)._aiSalesConcierge_ToggleProductCard = (msgIdx, prodIdx) => this.toggleCollapse(msgIdx, prodIdx);

    this.connectWS();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('myshopifydomain')) {
      this.connectWS();
    }
  }

  connectWS() {
    if (
      this.myshopifydomain &&
      (this.ws?.readyState !== WebSocket.CONNECTING && this.ws?.readyState !== WebSocket.OPEN)
    ) {
      this.ws = new WebSocket(`${CHAT_ENDPOINT}/ws`);
      this.ws.onopen = () => {
        this.requestUpdate()
      }
      this.ws.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'chunk') {
          this.addOrUpdateBotMessage(data.content);
        } else if (data.type === 'complete') {
          // Support recommended_products in the response!
          this.finishBotMessage(data.content, data.recommended_products);
        } else if (data.type === 'done') {
          this.loading = false;
          this.thinking = false;
        } else if (data.type === 'error') {
          this.handleWSError()
        }
      };
      this.ws.onclose = () => setTimeout(() => this.connectWS(), 1000);
    }
  }

  sendMessage() {
    if (this.myshopifydomain) {
      if (!this.input.trim() || this.loading) return;
      // Add user message
      this.messages = [
        ...this.messages,
        { role: 'user', content: this.input },
      ];
      // Add placeholder bot message (plus room for products)
      this.messages = [
        ...this.messages,
        {
          role: 'bot',
          content: '',
          isStreaming: true,
          recommended_products: [],
        },
      ];
      this.recommendedProductStates = [
        ...this.recommendedProductStates,
        [], // Each message can have its own product states array
      ];
      this.loading = true;
      this.thinking = false;
      this.ws.send(
        JSON.stringify({ question: this.input, shop_domain: this.myshopifydomain })
      );
      this.input = '';
      this.updateComplete.then(() => {
        this.scrollToBottom();
      });
    }
  }

  addOrUpdateBotMessage(chunk) {
    let lastIdx = this.messages.length - 1;
    if (lastIdx >= 0 && this.messages[lastIdx].role === 'bot') {
      // Thinking indicator (chunk may have <think>...</think> block)
      if (chunk.includes('<think>')) {
        this.thinking = true;
      } else {
        this.thinking = false;
      }
      // Remove <think> blocks from display
      chunk = chunk.replace(/<think>.*?<\/think>/gs, '');
      this.messages[lastIdx].content += chunk;
      this.requestUpdate();
      this.scrollToBottom();
    }
  }

  highlightProductTitles(content: string, recommendedProducts: any[], msgIdx: number): string {
    let newContent = content;
    if (!recommendedProducts) return content;

    recommendedProducts.forEach((item, idx) => {
      const matched = item.matched_substring?.trim();
      if (!matched) return;

      // Escape for safe regex
      const escaped = matched.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b(${escaped})\\b`, 'gi'); // case-insensitive, word-boundary match

      newContent = newContent.replace(
        regex,
        `<span class="underline text-primary cursor-pointer" onclick="window._aiSalesConcierge_ToggleProductCard(${msgIdx}, ${idx})">$1</span>`
      );
    });

    return newContent;
  }

  finishBotMessage(fullContent, recommendedProducts) {
    let lastIdx = this.messages.length - 1;
    if (lastIdx >= 0 && this.messages[lastIdx].role === 'bot') {
      try {
      fullContent = fullContent.replace(/<think>.*?<\/think>/gs, '');
      fullContent = this.highlightProductTitles(fullContent, recommendedProducts, lastIdx);

      // Support recommended_products field
      this.messages[lastIdx] = {
        role: 'bot',
        content: fullContent,
        isStreaming: false,
        recommended_products: recommendedProducts || [],
      };
      // Collapse state: expand the first product only
      const productStates =
        recommendedProducts && (recommendedProducts.length === 1 ? [true] :
          recommendedProducts.map((_, i) => false)//i === 0)
        ) || [];
      this.recommendedProductStates[lastIdx] = productStates;
      } catch (e) {
        console.error(e)
      }

      this.thinking = false;
      this.loading = false;
      this.requestUpdate();
      this.scrollToBottom();

      this.selectedVariants[lastIdx] = recommendedProducts.map(p => p.product.variants?.[0]?.id || null);
    }
  }

  toggleCollapse(msgIdx, prodIdx) {
    this.recommendedProductStates = [
      ...this.recommendedProductStates.slice(0, msgIdx),
      [
        ...this.recommendedProductStates[msgIdx].slice(0, prodIdx),
        !this.recommendedProductStates[msgIdx][prodIdx],
        ...this.recommendedProductStates[msgIdx].slice(prodIdx + 1),
      ],
      ...this.recommendedProductStates.slice(msgIdx + 1),
    ];
    this.requestUpdate();
  }

  addToCart(variantId) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => r.json())
      .then(() => {
        alert('Added to cart!');
      });
  }

  buyNow(variantId) {
    // Go directly to checkout with only this item (standard Shopify pattern)
    window.location.href = `/cart/${variantId}:1`;
  }

  getVariantId(gid: string) {
    const match = gid.match(/ProductVariant\/(\d+)/);
    const id = match ? match[1] : null;
    return id
  }

  getSelectedVariantPrice(product, msgIdx, prodIdx) {
    const selectedId = this.selectedVariants[msgIdx]?.[prodIdx];
    const match = product.variants?.find(v => v.id == selectedId);
    if (!match) return '';
    return `$${parseFloat(match.price).toFixed(2)}`;
  }

  handleVariantChange(msgIdx, prodIdx, newId) {
    const updated = [...(this.selectedVariants[msgIdx] || [])];
    updated[prodIdx] = newId;
    this.selectedVariants = [
      ...this.selectedVariants.slice(0, msgIdx),
      updated,
      ...this.selectedVariants.slice(msgIdx + 1)
    ];
  }

  getSelectedVariantImage(product, msgIdx, prodIdx) {
    const selectedId = this.selectedVariants[msgIdx]?.[prodIdx];
    const variant = product.variants?.find(v => v.id == selectedId);

    if (variant && variant.image_urls) return variant.image_urls.split(',')[0];

    const pvimg = product.product_image_urls.split(',').find(src => src.includes(selectedId))
    if (pvimg) return pvimg;

    return product.product_image_urls.split(',')[0] || null; // fallback to product default
  }

  isOutOfStock(variant) {
    return (
      variant.inventoryPolicy === 'DENY' &&
      (variant.inventoryQuantity ?? 0) < 1
    );
  }

  renderRecommendedProductCard(product, expanded, msgIdx, prodIdx) {
    const imgSrc = this.getSelectedVariantImage(product, msgIdx, prodIdx);

    const selectedId = this.selectedVariants[msgIdx]?.[prodIdx];
    const variant = product.variants?.find(v => v.id == selectedId)
                    || product.variants?.[0]; // fallback

    return html`
      <div class="transition-all duration-300 overflow-hidden" style="max-height: ${expanded ? 'none' : '0'};">
        <div class="p-4 border border-gray-200 rounded-xl shadow bg-white mb-4">
          <div class="flex gap-4 items-start">
            ${imgSrc
              ? html`<img src="${imgSrc}" class="w-28 h-28 object-cover rounded-md border border-gray-200" />`
              : null}
            <div class="flex-1">
              <a
                href="${product.url || `/products/${product.handle}`}"
                class="text-lg font-semibold underline text-gray-900 hover:text-primary"
                target="_blank" rel="noopener"
              >
                ${product.title || product.product_title}
              </a>

              <div class="text-sm text-gray-600 mt-1">
                ${(product.description || '').slice(0, 160)}${(product.description || '').length > 160 ? '…' : ''}
              </div>

              ${(product.variants?.length || 0) > 1 ? html`
                <div class="mt-2">
                  <select
                    class="select select-sm border-gray-300"
                    @change=${(e) => this.handleVariantChange(msgIdx, prodIdx, e.target.value)}
                  >
                    ${(product.variants || []).map(v => html`
                      <option value="${v.id}" ?selected=${this.selectedVariants[msgIdx]?.[prodIdx] == v.id}>
                        ${v.title} — $${parseFloat(v.price || product.price).toFixed(2)}
                      </option>
                    `)}
                  </select>
                </div>
              ` : null}

              <div class="text-lg font-bold text-gray-800 mt-3">
                $${parseFloat(variant?.price || product.price).toFixed(2)}
              </div>

              ${variant && product.tracksInventory && variant.inventoryItem?.tracked && this.isOutOfStock(variant) ? html`
                <div class="mt-3 text-sm font-medium text-red-600">
                  Out of Stock
                </div>
              ` : html`
                <div class="flex gap-2 mt-4">
                  <button
                    class="bg-gray-100 hover:bg-gray-200 text-sm text-gray-800 font-medium py-1.5 px-3 rounded border"
                    @click=${() => this.addToCart(this.getVariantId(selectedId))}
                  >
                    ${this.locale.addToCart}
                  </button>
                  <button
                    class="bg-primary text-white text-sm font-medium py-1.5 px-3 rounded hover:bg-primary-focus"
                    @click=${() => this.buyNow(this.getVariantId(selectedId))}
                  >
                    ${this.locale.buyNow}
                  </button>
                </div>
              `}
            </div>
            <button class="ml-2 text-gray-400 hover:text-gray-600"
              @click=${() => this.toggleCollapse(msgIdx, prodIdx)}
              aria-label="Close product card"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    `;
  }

  scrollToBottom() {
    const scroller = this.renderRoot.querySelector('#chat-messages');
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }

  handleWSError(msg) {
    this.wsError = true;
    this.loading = false;
    this.thinking = false;
    // Only append error once per failed send
    if (!this.messages.length || this.messages[this.messages.length - 1].role !== 'error') {
      this.messages = [
        ...this.messages,
        { role: 'error', content: msg || this.locale.retryMsg }
      ];
    }
    this.requestUpdate();
    this.scrollToBottom();
  }

  newChat() {
    this.messages = [];
    this.loading = false
    this.recommendedProductStates = [];
    this.input = '';
    this.wsError = false;
    this.connectWS();
  }

  // --- Minimize/Expand ---
  minimizeChat() { this.minimized = true; }
  openChat() { this.minimized = false; this.updateComplete.then(() => this.scrollToBottom()); }

  get chatContainerClasses() {
    return [
      "fixed bottom-4 right-4 max-w-xl w-full sm:w-[28rem] z-100 transition-all duration-300 rounded-xl shadow-2xl border bg-base-200 flex flex-col overflow-hidden",
      this.minimized ? "h-0 pointer-events-none opacity-0" : "h-[600px] pointer-events-auto opacity-100"
    ].join(" ");
  }

  render() {
    const locale = { ...DEFAULT_LOCALE, ...(this.locale || {}) };

    return this.ws?.readyState === WebSocket.OPEN ? html`
      <!-- collapsed bubble -->
      <div class="${this.minimized ? 'fixed bottom-6 right-6 z-100 flex gap-2 cursor-pointer bg-transparent' : 'hidden'}"
          @click=${() => this.openChat()}
          data-theme="${this.colorscheme || 'light'}"
      >
        <div class="chat chat-end " style="transform: translate(0, -16px)">
          <div class="chat-bubble bg-info text-white"
          >${this.openingMessage}</div>
        </div>

        <button
          class="chat-fab bg-base-100 border border-base-300 text-white rounded-full shadow-xl flex items-center gap-2 px-3 py-2 cursor-pointer"
          aria-label="Open chat"
          style="min-width:56px;"
        >
          <span class="flex items-center justify-center w-12 h-12">
            <img src="${this.avatarurl}" />
          </span>
        </button>
      </div>

      <div class="${this.chatContainerClasses}"></div> <!-- For smooth transition out -->

      <!-- chat panel -->
      <div class="border-base-300 ${this.chatContainerClasses}" data-theme="${this.colorscheme || 'light'}"
      >
        <div class="rounded-xl shadow-2xl bg-base-200 flex flex-col h-[600px] overflow-hidden">
          <div class="flex items-center justify-between p-2 border-b border-primary-content bg-base-100 rounded-t-xl"
          >
            <div class="flex items-center gap-2 font-bold text-base">
              <img src="${this.avatarurl}" class="w-7 h-7 rounded-full" alt="AI Bot" />
              ${this.chatTitle}
            </div>

            <div class="flex gap-1">
              <button class="btn btn-sm btn-outline ${this.messages.length > 0 ? '' : 'hidden'}" @click=${() => this.newChat()}>
                ${locale.newChat}
              </button>
              <button class="btn btn-sm btn-circle" @click=${() => this.minimizeChat()} aria-label="Minimize chat">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <div id="chat-messages" class="flex-1 overflow-auto p-4 space-y-3 bg-base-200">
            ${this.messages.map((msg, msgIdx) => {
      if (msg.role === 'user') {
        return html`
                  <div class="chat chat-end">
                    <div class="chat-bubble bg-primary text-white">${msg.content}</div>
                  </div>
                `;
      }
      if (msg.role === 'error') {
        return html`
                  <div class="chat chat-start">
                    <div class="chat-bubble bg-error text-white">${msg.content}</div>
                  </div>
                `;
      }
      return html`
                <div class="chat chat-start ">
                  <div class="chat-image avatar">
                    <div class="w-10 rounded-full">
                      <img src="${this.avatarurl}" />
                    </div>
                  </div>
                  <div class="chat-bubble bg-base-100 text-base-content break-words">
                    <div class="prose max-w-full" .innerHTML=${marked.parse(msg.content || '')}></div>
                    ${msg.isStreaming && this.loading
          ? html`<span class="loading loading-dots loading-xs ml-2"></span>`
          : ''}
                    ${(msg.recommended_products && msg.recommended_products.length)
          ? msg.recommended_products.map((prod, prodIdx) =>
            this.renderRecommendedProductCard(
              prod.product,
              (this.recommendedProductStates[msgIdx] || [])[prodIdx] || false,
              msgIdx,
              prodIdx
            )
          )
          : ''
        }
                  </div>
                </div>
              `;
    })}
            ${this.thinking
        ? html`<div class="chat chat-start">
                    <div class="chat-bubble bg-base-100 text-base-content animate-pulse">
                      <span>🤔 ${locale.thinking}</span>
                    </div>
                  </div>`
        : ''}
          </div>

          <form
            class="p-4 flex gap-2 bg-base-100 border-t border-primary-content"
            @submit=${(e) => {
        e.preventDefault();
        this.sendMessage();
      }}
          >
            <input
              class="input input-bordered flex-1 text-base-content"
              .value=${this.input}
              ?disabled=${this.loading}
              @input=${(e) => (this.input = e.target.value)}
              @keydown=${(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        }
      }}
              placeholder=${locale.placeholder}
              autocomplete="off"
            />
            <button
              class="btn btn-primary"
              ?disabled=${this.loading || !this.input.trim()}
              type="submit"
            >
              ${this.loading
        ? html`<span class="loading loading-spinner"></span>`
        : html`${locale.send}`}
            </button>
          </form>
        </div>
      </div>
    ` : null;
  }
}
